import type {FeatureCollection} from 'geojson'
import {baseApi} from './base'
import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {CockpitElementStatus} from '@interfaces/cockpitStatus'
import type {CockpitViolations} from '@interfaces/cockpitViolations'
import type {CockpitWarningInterface} from '@interfaces/cockpitWarning'
import type {DisconnectedSignalCountsResponse, Signal} from '@interfaces/disconnectedSignals'
import {DEFAULT_LARGE_SELECTION_LIST_LIMIT} from '@shared/constants/preferences/pagination'

export const cockpitAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCockpitDisconnectedSignals: builder.query<
      Signal[],
      {
        lineId?: string
        equipmentId?: string
      }
    >({
      query: ({lineId, equipmentId}) => ({
        url: 'disconnected-signals/',
        params: {machine_id: lineId, machine_sub_system_id: equipmentId},
      }),
      transformResponse: (response: BaseFetchResultsInterface<Signal>) => response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getCockpitDisconnectedSignalCounts: builder.query<
      DisconnectedSignalCountsResponse,
      {
        lineIds?: string[]
        equipmentIds?: string[]
      }
    >({
      query: ({lineIds = [], equipmentIds = []}) => ({
        url: 'disconnected-signals/counts/',
        params: {
          machine_ids: lineIds.join(','),
          machine_sub_system_ids: equipmentIds.join(','),
        },
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    // The map's static part -- placed elements, names, floors, shapes -- only
    // changes on an admin edit, which invalidates these tags. So it is fetched
    // once and kept, not refetched on every visit; the backend caches it and
    // answers a revalidation of an unchanged map with a body-less 304.
    getCockpitGeometry: builder.query<FeatureCollection, void>({
      query: () => 'cockpit/geometry/',
      keepUnusedDataFor: 60 * 60,
      providesTags: ['ProductionSite', 'ProductionArea', 'Line', 'Equipment', 'Floor'],
    }),
    // The live part: per element, the latest batch's classification and the
    // warning flag. Light, so it is refetched on mount and polled. `floorId`
    // limits it to the elements shown on that floor; `at` (UTC ISO) asks for
    // the status at that past moment instead of now (AB#6669).
    getCockpitStatus: builder.query<
      CockpitElementStatus[],
      {floorId?: string | null; at?: string | null}
    >({
      query: ({floorId, at}) => ({
        url: 'cockpit/status/',
        params: {...(floorId ? {floor: floorId} : {}), ...(at ? {at} : {})},
      }),
    }),
    // The open threshold violations of one equipment or one line (every
    // equipment on it), plus the thresholds that cannot be trusted right now.
    // Unpaginated. Tagged so the top bar's refresh reloads the open panel
    // together with the map status.
    // `at` (UTC ISO): the violations open at that past moment (AB#6669).
    getCockpitViolations: builder.query<
      CockpitViolations,
      {equipmentId?: string; lineId?: string; at?: string | null}
    >({
      query: ({equipmentId, lineId, at}) => ({
        url: 'cockpit/violations/',
        params: {
          ...(equipmentId ? {equipment: equipmentId} : {line: lineId}),
          ...(at ? {at} : {}),
        },
      }),
      providesTags: ['CockpitViolations'],
    }),
    getCockpitWarningsSignalBased: builder.query<
      CockpitWarningInterface[],
      {
        equipmentId?: string
        lineId?: string
      }
    >({
      // Ask for every warning: without a limit the backend returns only its
      // default page of 25, cutting off a line's lights past the 25th.
      query: ({equipmentId, lineId}) => ({
        url: 'cockpitwarningssignalbased/',
        params: {
          ...(equipmentId
            ? {signal__machine_sub_system: equipmentId}
            : {signal__machine_sub_system__machine: lineId}),
          limit: DEFAULT_LARGE_SELECTION_LIST_LIMIT,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<CockpitWarningInterface>) =>
        response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetCockpitDisconnectedSignalCountsQuery,
  useGetCockpitDisconnectedSignalsQuery,
  useGetCockpitGeometryQuery,
  useGetCockpitStatusQuery,
  useGetCockpitViolationsQuery,
  useGetCockpitWarningsSignalBasedQuery,
} = cockpitAPI
